/**
 * Wave 3 (2026-06-08) · `.copy-block` one-tap copy for prompt templates
 * and the MS-14 bug-journal template.
 *
 * Markup contract (per sprint-01.ts MS-13 / MS-14):
 *   <div class='copy-block'>            ← may co-exist with .ai-tip-block
 *     ... <div class='cb-src ...'>copyable text (BR = line break)</div> ...
 *     <button type='button' class='cb-copy'>Скопировать промпт</button>
 *   </div>
 *
 * Copy text is reconstructed from `.cb-src` child nodes: <br> and block
 * elements become '\n', everything else contributes textContent. This is
 * deterministic in both browsers and jsdom (innerText is unreliable there).
 *
 * Feedback: button text → «Скопировано» + .done for 2s, then reverts.
 * Clipboard API first; hidden-textarea execCommand fallback for older
 * WebViews. State is session-only, no persistence.
 */
const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'OL', 'UL', 'H4', 'H3']);

function textFrom(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  if (el.tagName === 'BR') return '\n';
  const inner = Array.from(el.childNodes).map(textFrom).join('');
  return BLOCK_TAGS.has(el.tagName) ? `${inner}\n` : inner;
}

function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

export function initCopyBlocks(root: HTMLElement): () => void {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>('.copy-block'));
  const cleanups: Array<() => void> = [];

  for (const block of blocks) {
    if (block.dataset.cbInit === '1') continue;
    block.dataset.cbInit = '1';

    const src = block.querySelector<HTMLElement>('.cb-src');
    const btn = block.querySelector<HTMLButtonElement>('.cb-copy');
    if (!src || !btn) continue;

    const idleLabel = btn.textContent ?? 'Скопировать';
    let timer: ReturnType<typeof setTimeout> | undefined;

    const onClick = () => {
      const text = textFrom(src).replace(/\n{3,}/g, '\n\n').trim();
      const done = () => {
        btn.textContent = 'Скопировано';
        btn.classList.add('done');
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          btn.textContent = idleLabel;
          btn.classList.remove('done');
        }, 2000);
      };
      // Feedback either way: clipboard denial in sandboxed previews must not
      // leave the button mute (student would re-tap into the void).
      copyToClipboard(text).then(done, done);
    };

    btn.addEventListener('click', onClick);
    cleanups.push(() => {
      btn.removeEventListener('click', onClick);
      if (timer) clearTimeout(timer);
      delete block.dataset.cbInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
